/**
 * Team + folder facets for the customer device list.
 *
 * These are two DIFFERENT things and support kept conflating them. A screen's
 * owning team (`device.teamId`) and the folder it sits in (`device.path`) are
 * independent: an account can keep every screen in the root team and organise
 * them into a folder per site, then share each folder with that site's team.
 * The team then "has" screens in the portal while owning none — so a team
 * rollup reads 0 and the screens are only findable by folder.
 *
 * Hence two separate pickers rather than one blended search box: picking a team
 * answers "what does this team own", picking a folder answers "what is filed
 * here", and the two counts disagreeing is the useful signal, not a bug.
 */

/** One selectable row in a facet picker. `null` is the "not set" bucket. */
export interface FacetOption {
  /** Team id / folder path, or `null` for the no-team / no-folder bucket. */
  value: string | null;
  label: string;
  /** Rows carrying this value. Omitted when the picker has no counts to show (Account Audit Log). */
  count?: number;
}

/**
 * One picked value: a team id / folder path, or `null` for the "not set"
 * bucket (no team / no folder).
 */
export type FacetValue = string | null;

/**
 * A facet selection is a set of picked values. Empty = no filter.
 *
 * Values within a facet are ORed (three sites at once), while the team and
 * folder facets are ANDed with each other — the usual faceted-search
 * convention, and the combination "owned by X, filed under Y" is a question
 * support actually asks.
 */
export type FacetSelection = readonly FacetValue[];

export const NO_SELECTION: FacetSelection = [];

/** Toggle a value in/out of a selection, preserving pick order. */
export function toggleFacetValue(
  selection: FacetSelection,
  value: FacetValue,
): FacetValue[] {
  return selection.includes(value)
    ? selection.filter((v) => v !== value)
    : [...selection, value];
}

/** The device fields these helpers read — a slice of `MdmDevice`. */
export interface FacetDevice {
  teamId?: string;
  teamName?: string | null;
  path?: string | null;
}

export const NO_TEAM_LABEL = 'No team';
export const NO_FOLDER_LABEL = 'No folder (top level)';

/**
 * A device can carry a teamId that no longer resolves to a team on the account
 * (team deleted, device left behind) — the backend rollup folds these into
 * "Unassigned". Label them so a bare Mongo id never reads as a team name.
 */
export function unknownTeamLabel(teamId: string): string {
  return `Unknown team (${teamId})`;
}

/**
 * `staffCustomerTeams` appends a synthetic rollup row for devices with no team
 * so its buckets sum to the account total. It is not a team and no device
 * carries it as a teamId — picking it could only ever return nothing, so it is
 * dropped here in favour of this file's own `No team` bucket.
 */
export const SYNTHETIC_UNASSIGNED_TEAM_ID = '__unassigned__';

/** Trailing slashes and blanks normalise to `null` so "/" and "" are one bucket. */
export function normalizeFolderPath(path?: string | null): string | null {
  if (!path) return null;
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? null : trimmed;
}

/**
 * Team options: every team that owns devices, PLUS every team on the account
 * that owns none. The zero rows are the point — searching "Mattingley" and
 * seeing `MATTINGLEY — 0` is what tells an agent to look in folders instead.
 */
export function buildTeamOptions(
  devices: readonly FacetDevice[],
  accountTeams: ReadonlyArray<{ _id: string; name: string }> = [],
): FacetOption[] {
  const counts = new Map<string, number>();
  const labels = new Map<string, string>();
  let noTeam = 0;

  for (const d of devices) {
    if (!d.teamId) {
      noTeam += 1;
      continue;
    }
    counts.set(d.teamId, (counts.get(d.teamId) ?? 0) + 1);
    if (d.teamName) labels.set(d.teamId, d.teamName);
  }

  for (const t of accountTeams) {
    if (t._id === SYNTHETIC_UNASSIGNED_TEAM_ID) continue;
    if (!counts.has(t._id)) counts.set(t._id, 0);
    if (!labels.has(t._id)) labels.set(t._id, t.name);
  }

  const options: FacetOption[] = [...counts.entries()].map(([value, count]) => ({
    value,
    label: labels.get(value) ?? unknownTeamLabel(value),
    count,
  }));

  // Owners first (that's what the picker is for), then alphabetical so the long
  // tail of empty teams stays in a predictable order.
  options.sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.label.localeCompare(b.label));

  if (noTeam > 0) options.push({ value: null, label: NO_TEAM_LABEL, count: noTeam });
  return options;
}

/**
 * Folder options, one row per folder in the tree — including ancestors that
 * hold no devices directly. Counts are SUBTREE counts so a row's number always
 * matches what selecting it shows: `/KONA ICE` counts everything filed beneath
 * it, `/KONA ICE/Mattingley` counts just that site.
 */
export function buildFolderOptions(devices: readonly FacetDevice[]): FacetOption[] {
  const counts = new Map<string, number>();
  let noFolder = 0;

  for (const d of devices) {
    const path = normalizeFolderPath(d.path);
    if (path === null) {
      noFolder += 1;
      continue;
    }
    const segments = path.split('/').filter(Boolean);
    let prefix = '';
    for (const segment of segments) {
      prefix += `/${segment}`;
      counts.set(prefix, (counts.get(prefix) ?? 0) + 1);
    }
  }

  // Alphabetical: a folder always sorts immediately above its own children, so
  // the flat list still reads as a tree.
  const options: FacetOption[] = [...counts.entries()]
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (noFolder > 0) {
    options.unshift({ value: null, label: NO_FOLDER_LABEL, count: noFolder });
  }
  return options;
}

/** Facet rows whose name matches a free-text query, for the search suggestions. */
export interface FacetMatches {
  teams: FacetOption[];
  folders: FacetOption[];
  /** Totals before the display cap, so the UI can say "+3 more". */
  teamTotal: number;
  folderTotal: number;
}

/** The last segment of a folder path — what a person actually types. */
function leafName(path: string): string {
  return path.split('/').filter(Boolean).pop() ?? path;
}

/**
 * Teams and folders whose NAME matches the device-search query.
 *
 * The search box deliberately matches device names only, so typing a site name
 * finds nothing on an account that files screens by folder — the exact dead end
 * this feature exists to fix. These matches let the UI offer the folder (or
 * team) the query really meant, without blending the two into one search.
 *
 * The "not set" buckets are excluded: "No team" is a bucket, not a name, and
 * matching it on a query like "no" would be noise.
 */
export function matchingFacets(
  query: string,
  teamOptions: readonly FacetOption[],
  folderOptions: readonly FacetOption[],
  limit = 3,
): FacetMatches {
  const q = query.trim().toLowerCase();
  // One character matches nearly everything — not a suggestion, just noise.
  if (q.length < 2) return { teams: [], folders: [], teamTotal: 0, folderTotal: 0 };

  const rankTeam = (o: FacetOption) => {
    const l = o.label.toLowerCase();
    if (!l.includes(q)) return null;
    return l.startsWith(q) ? 0 : 1;
  };
  const rankFolder = (o: FacetOption) => {
    const leaf = leafName(o.label).toLowerCase();
    if (leaf.startsWith(q)) return 0;
    if (leaf.includes(q)) return 1;
    // Fall back to the full path so "/kona ice/matt" still finds something.
    return o.label.toLowerCase().includes(q) ? 2 : null;
  };

  const pick = (options: readonly FacetOption[], rank: (o: FacetOption) => number | null) =>
    options
      .filter((o) => o.value !== null)
      .map((o) => ({ o, r: rank(o) }))
      .filter((x): x is { o: FacetOption; r: number } => x.r !== null)
      // Best match first, then the row that actually holds screens.
      .sort((a, b) => a.r - b.r || (b.o.count ?? 0) - (a.o.count ?? 0) || a.o.label.localeCompare(b.o.label))
      .map((x) => x.o);

  const teams = pick(teamOptions, rankTeam);
  const folders = pick(folderOptions, rankFolder);
  return {
    teams: teams.slice(0, limit),
    folders: folders.slice(0, limit),
    teamTotal: teams.length,
    folderTotal: folders.length,
  };
}

/** Empty selection matches everything; `null` matches devices with no team. */
export function matchesTeam(device: FacetDevice, selection: FacetSelection): boolean {
  if (selection.length === 0) return true;
  return selection.some((v) => (v === null ? !device.teamId : device.teamId === v));
}

/**
 * Subtree match — a picked folder includes everything filed below it. Picking
 * several is a union, so overlapping picks (`/KONA ICE` and one of its
 * children) can't double-count a device.
 */
export function matchesFolder(device: FacetDevice, selection: FacetSelection): boolean {
  if (selection.length === 0) return true;
  const path = normalizeFolderPath(device.path);
  return selection.some((v) => {
    if (v === null) return path === null;
    if (path === null) return false;
    return path === v || path.startsWith(`${v}/`);
  });
}
